import { isNullish } from '@sas/nova-core/typeguards';
import { generateSingleDisplayData } from './DualSelectorLayout/DualSelectorLayout';
export function isItemSelectable(item) {
  return !(item._disabled ?? false) && !item.hasChildren;
}
export const getDualSelectorReducer = (mode, onTargetChange, selectedItemsThreshold) => (state, action) => dsReducer(state, action, mode, onTargetChange, selectedItemsThreshold);
function dsReducer(state, action, mode, onTargetChange, selectedItemsThreshold) {
  let actionQueue;
  let newSourceData = [];
  let newTargetData = [];
  let newTargetList = [];
  let targetDataArr = [];
  const customItems = state.customItems ? [...state.customItems] : [];
  let newSelectedRowsArray = [];
  let initialIndex = 0;
  let selectedRowsNumberArray = [];
  const onTargetDataChange = (items, reason, selectedTargetListIndex) => {
    const cleanItems = removeInternalFields(items);
    onTargetChange(cleanItems, reason, selectedTargetListIndex);
  };
  if (action.type !== 'filter' && action.type !== 'custom_add' && action.type !== 'reset') {
    selectedRowsNumberArray = action.payload.selectedRows.map(item => parseInt(item));
  }
  state.focusAfterAction = action.type === 'selection' ? false : true;
  state.isDataReset = action.type === 'reset' ? true : false;

  // always make sure source data is sorted - for correct parent/child dependency
  state.sourceData.sort((a, b) => {
    const key = 'index';
    let comparison = 0;
    if (a[key] > b[key]) {
      comparison = 1;
    } else if (a[key] < b[key]) {
      comparison = -1;
    }
    return comparison;
  });
  const shouldApplySelectedItemsThreshold = !isNullish(selectedItemsThreshold) && selectedItemsThreshold > 0;
  switch (action.type) {
    case 'custom_add':
      // custom items feature is not supported in one to two DualSelector
      newSourceData = [...state.sourceData];
      targetDataArr = [...state.targetData];
      const targetList = [...targetDataArr[action.payload.layoutIndex ?? 0]];
      if (shouldApplySelectedItemsThreshold) {
        if ((action.payload.selectedRows?.length ?? 0) + targetList.length > selectedItemsThreshold) {
          return state;
        }
      }
      actionQueue = [{
        from: 'customField',
        button: 'customField',
        currentSourceData: [...state.sourceData],
        currentTargetData: [...state.targetData[0]],
        lastItemSelection: []
      }];
      targetList.push(action.payload.selectedRows);
      customItems.push(action.payload.selectedRows);
      state.narrator = {
        selectedRows: 1,
        direction: action.type,
        action: 'customAdd'
      };
      const newTargetFilteredData = filteDataBySearchValueInField(targetList, state.targetSearchValue?.[0],
      // in case of custom items, there will be only one target list
      state.key);
      onTargetDataChange(targetList, 'customControlAdd', state.selectedTableIndex);
      targetDataArr[0] = targetList; // In case of custom items there will be only one target list
      const targetFilteredDataArr = newTargetFilteredData && [newTargetFilteredData];
      return {
        ...state,
        ...getUpdateSourceAndFilteredData(state.sourceData, targetList, mode, selectedItemsThreshold, state.sourceSearchValue, state.key),
        targetData: targetDataArr,
        targetFilteredData: targetFilteredDataArr,
        currentActionQueue: actionQueue,
        customItems,
        itemSelections: newSelectedRowsArray
      };
    case 'add':
      return handleAddAction(state, action.payload, onTargetDataChange, selectedItemsThreshold);
    case 'remove':
      return handleRemoveAction(state, action.payload, onTargetDataChange, selectedItemsThreshold);
    case 'undo':
      const currentActionQueue = state.currentActionQueue;
      let undoTo = 'add';
      let selectionTable;
      let newTargetFilteredList = [];
      const index = currentActionQueue[0]?.selectedTableIndex ?? 0;
      let lastItemSelection = [];
      if (currentActionQueue && currentActionQueue.length > 0) {
        undoTo = currentActionQueue[0].from === 'source' ? 'add' : 'remove';
        if (currentActionQueue[0].from === 'source' || currentActionQueue[0].from === 'target') {
          selectionTable = currentActionQueue[0].from;
        }

        /* -------------------------------------------
         *   Items should be removed from the filtered list as well as a part of undo
         * ---------------------------------------------*/

        newSourceData = [...currentActionQueue[0].currentSourceData];
        newTargetData = [...currentActionQueue[0].currentTargetData];
        if (currentActionQueue[0].button === 'customField') {
          state.customItems?.splice(state.customItems.length - 1, 1);
        }

        // Incase the target list is shuffled after the remove operation its state
        // is maintained following undo. But that is not the case for add.
        if (currentActionQueue[0].button === 'remove') {
          newSelectedRowsArray = currentActionQueue[0].lastItemSelection ? currentActionQueue[0].lastItemSelection : [];
          const lastSelectionRows = newSelectedRowsArray.map(row => parseInt(row)).sort(compareAscendingOrder);
          targetDataArr = [...state.targetData];
          newTargetData = [...targetDataArr[currentActionQueue[0].selectedTableIndex ?? 0]];
          const currentTargetData = currentActionQueue[0].currentTargetData;
          const lastTargetFilteredData = currentActionQueue[0].lastTargetFilteredData;
          const indexMapFromFilterToTotal = generateIndexMapFromFilteredToTotal(lastTargetFilteredData, currentTargetData);
          if (currentActionQueue[0].lastItemSelection) {
            for (const position of lastSelectionRows) {
              const positionInTotal = indexMapFromFilterToTotal[position];
              newTargetData.splice(positionInTotal, 0, currentTargetData[positionInTotal]);
            }
          }
        }

        // update the disabled state
        newSourceData = getSourceItemsWithDisabledStateUpdate(newSourceData, newTargetData, mode, selectedItemsThreshold);
        newTargetFilteredList = filteDataBySearchValueInField(newTargetData, state.targetSearchValue?.[0], state.key);
        state.sourceFilteredData = filteDataBySearchValueInField(newSourceData, state.sourceSearchValue, state.key);

        // Ensure selection aligns with the filter and remains correct after undo.
        const key = action.payload.key ?? '';
        const currentData = selectionTable === 'source' ? state.sourceFilteredData ?? newSourceData : newTargetFilteredList ?? newTargetData;
        const previousData = selectionTable === 'source' ? currentActionQueue[0].lastSourceFilteredData ?? newSourceData : currentActionQueue[0].lastTargetFilteredData ?? newTargetData;
        const currentDataMap = new Map();
        currentData.forEach((item, index) => {
          if (currentDataMap.has(item[key])) currentDataMap.get(item[key])?.push(index.toString());else currentDataMap.set(item[key], [index.toString()]);
        });
        lastItemSelection = currentActionQueue[0].lastItemSelection.reduce((acc, row) => {
          const prevItem = previousData[parseInt(row)][key];
          if (currentDataMap.has(prevItem)) {
            const prevItemIndices = currentDataMap.get(prevItem) ?? [];
            acc.push(prevItemIndices.shift() ?? '-1');
            currentDataMap.set(prevItem, prevItemIndices);
          }
          return acc;
        }, []);
      }
      /* -------------------------------------------
       *  Filtered value should not persist for Add All / Remove All
       * ---------------------------------------------*/
      let targetSearchValue;
      if (newSourceData.length === 0 && newTargetData.length > 0) {
        state.sourceSearchValue = '';
        state.sourceFilteredData = undefined;
      } else if (newSourceData.length > 0 && newTargetData.length === 0) {
        targetSearchValue = '';
        newTargetFilteredList = undefined;
      }
      state.narrator = {
        direction: action.type,
        action: 'undo',
        undoTo
      };
      onTargetDataChange(newTargetData, undefined, state.selectedTableIndex);
      const newData = [...state.targetData];
      newData[currentActionQueue[0].selectedTableIndex ?? 0] = newTargetData;
      const newFilteredData = state.targetFilteredData && [...state.targetFilteredData];
      if (newFilteredData) newFilteredData[index] = newTargetFilteredList;
      const targetSearchValueArr = state.targetSearchValue && [...state.targetSearchValue];
      if (targetSearchValueArr && (targetSearchValue || targetSearchValue === '')) {
        targetSearchValueArr[index] = targetSearchValue;
      }
      return {
        ...state,
        sourceData: newSourceData,
        targetData: newData,
        currentActionQueue: [],
        selectionTable,
        itemSelections: selectionTable ? lastItemSelection : undefined,
        targetFilteredData: newFilteredData,
        targetSearchValue: targetSearchValueArr
      };
    case 'up':
      targetDataArr = [...state.targetData];
      // make a copy to avoid state update
      newTargetList = [...targetDataArr[state.selectedTableIndex ?? 0]];
      initialIndex = 0;
      selectedRowsNumberArray.sort(compareAscendingOrder);
      for (let index = 0; index < selectedRowsNumberArray.length; index++) {
        initialIndex = selectedRowsNumberArray[index];
        newTargetList = moveItems(newTargetList, initialIndex, initialIndex - 1);
        newSelectedRowsArray.push((initialIndex - 1).toString());
      }
      state.narrator = {
        direction: action.type,
        action: 'reorder'
      };
      onTargetDataChange(newTargetList, 'reorder', state.selectedTableIndex);
      targetDataArr[state.selectedTableIndex ?? 0] = newTargetList;
      return {
        ...state,
        sourceData: state.sourceData,
        targetData: targetDataArr,
        itemSelections: newSelectedRowsArray
      };
    case 'down':
      initialIndex = 0;
      targetDataArr = [...state.targetData];
      newTargetList = [...targetDataArr[state.selectedTableIndex ?? 0]];
      selectedRowsNumberArray.sort(compareDescendingOrder);
      for (let index = 0; index < selectedRowsNumberArray.length; index++) {
        initialIndex = selectedRowsNumberArray[index];
        newTargetList = moveItems(newTargetList, initialIndex, initialIndex + 1);
        newSelectedRowsArray.push((initialIndex + 1).toString());
      }
      state.narrator = {
        direction: action.type,
        action: 'reorder'
      };
      onTargetDataChange(newTargetList, 'reorder', state.selectedTableIndex);
      targetDataArr[state.selectedTableIndex ?? 0] = newTargetList;
      return {
        ...state,
        sourceData: state.sourceData,
        targetData: targetDataArr,
        itemSelections: newSelectedRowsArray
      };
    case 'top':
      initialIndex = 0;
      let finalIndex = 0;
      targetDataArr = [...state.targetData];
      newTargetList = [...targetDataArr[state.selectedTableIndex ?? 0]];
      selectedRowsNumberArray.sort(compareAscendingOrder);
      for (let index = 0; index < selectedRowsNumberArray.length; index++) {
        if (newTargetList[action.payload.selectedRows[index]]) {
          initialIndex = selectedRowsNumberArray[index];
          newTargetList = moveItems(newTargetList, initialIndex, finalIndex);
          newSelectedRowsArray.push(finalIndex.toString());
        }
        finalIndex++;
      }
      state.narrator = {
        direction: action.type,
        action: 'reorder'
      };
      onTargetDataChange(newTargetList, 'reorder', state.selectedTableIndex);
      targetDataArr[state.selectedTableIndex ?? 0] = newTargetList;
      return {
        ...state,
        sourceData: state.sourceData,
        targetData: targetDataArr,
        itemSelections: newSelectedRowsArray
      };
    case 'bottom':
      initialIndex = 0;
      targetDataArr = [...state.targetData];
      newTargetList = [...targetDataArr[state.selectedTableIndex ?? 0]];
      let movedIndex = newTargetList.length;
      selectedRowsNumberArray.sort(compareDescendingOrder);
      for (let index = 0; index < selectedRowsNumberArray.length; index++) {
        movedIndex--;
        if (newTargetList[action.payload.selectedRows[index]]) {
          initialIndex = selectedRowsNumberArray[index];
          newTargetList = moveItems(newTargetList, initialIndex, movedIndex);
          newSelectedRowsArray.push(movedIndex.toString());
        }
      }
      state.narrator = {
        direction: action.type,
        action: 'reorder'
      };
      onTargetDataChange(newTargetList, 'reorder', state.selectedTableIndex);
      targetDataArr[state.selectedTableIndex ?? 0] = newTargetList;
      return {
        ...state,
        sourceData: state.sourceData,
        targetData: targetDataArr,
        itemSelections: newSelectedRowsArray
      };
    case 'filter':
      const searchText = escapeRegExp(action.payload.searchValue);
      const regExp = new RegExp(searchText, 'gi');
      let newTargetSearchValue;
      let filteredTargetList;
      let newTargetSearchValueArr = state.targetSearchValue;
      let newTargetFilteredDataArr = state.targetFilteredData;
      state.narrator = {
        action: 'filter'
      };
      state.key = action.payload.key;
      if (action.payload.table === 'source') {
        if (!action.payload.searchValue) {
          state.sourceFilteredData = undefined;
          state.sourceSearchValue = action.payload.searchValue;
        } else {
          state.sourceSearchValue = action.payload.searchValue;
          state.sourceFilteredData = state.sourceData.filter(item => {
            return item[action.payload.key].match(regExp) || item.hasChildren;
          });
        }
      } else {
        newTargetSearchValue = action.payload.searchValue;
        if (!newTargetSearchValue) {
          filteredTargetList = undefined;
        } else {
          filteredTargetList = state.targetData[action.payload.layoutIndex ?? 0].filter(id => {
            return id[action.payload.key].match(regExp);
          });
        }
        if (newTargetSearchValueArr === undefined) newTargetSearchValueArr = [];
        if (newTargetFilteredDataArr === undefined) newTargetFilteredDataArr = [];
        newTargetFilteredDataArr[action.payload.layoutIndex ?? 0] = filteredTargetList;
        newTargetSearchValueArr[action.payload.layoutIndex ?? 0] = newTargetSearchValue;
      }
      state.itemSelections = [];
      if (action.payload.table === 'target') onTargetDataChange(filteredTargetList ?? [], 'filter', action.payload.layoutIndex);
      return {
        ...state,
        selectionTable: action.payload.table,
        targetSearchValue: newTargetSearchValueArr,
        targetFilteredData: newTargetFilteredDataArr,
        selectedTableIndex: action.payload.layoutIndex
      };
    case 'selection':
      return {
        ...state,
        itemSelections: action.payload.selectedRows,
        selectionTable: action.payload.tableName,
        selectedTableIndex: action.payload.selectedTableIndex
      };
    case 'delete':
      const preTargetData = shallowCloneItems(state.targetData);
      const preTargetFilteredData = shallowCloneItems(state.targetFilteredData?.[state.selectedTableIndex ?? 0] ?? []);
      newSourceData = [...state.sourceData];
      targetDataArr = [...state.targetData];
      newTargetList = [...targetDataArr[action.payload.layoutIndex ?? 0]];
      // in case of custom items there will be only one target list
      const targetFilteredData = state.targetFilteredData?.[0];
      const targetFilteredList = targetFilteredData && [...targetFilteredData];
      if (targetFilteredList?.length && state.itemSelections?.length) {
        const items = [];
        for (const selectedValues of state.itemSelections) {
          items.push(targetFilteredList[Number(selectedValues)]);
        }
        for (const item of items) {
          newTargetList.splice(newTargetList.indexOf(item), 1);
          targetFilteredData?.splice(targetFilteredData.indexOf(item), 1);
        }
        const nextSelectedItemIndex = getNextSelectableTargetItemIndex(selectedRowsNumberArray, preTargetFilteredData ?? preTargetData[action.payload.layoutIndex ?? 0], action.payload.targetDisplayStyle ?? 'multiple');
        newSelectedRowsArray = nextSelectedItemIndex !== undefined ? [nextSelectedItemIndex.toString()] : [];
      } else {
        const result = moveButtonAction(newTargetList, [], selectedRowsNumberArray, action.payload.targetDisplayStyle);
        newTargetList = result?.newSourceData;
        newSelectedRowsArray = result?.newSelectedRowsArray;
        onTargetDataChange(newTargetList, undefined, state.selectedTableIndex);
      }
      targetDataArr[action.payload.layoutIndex ?? 0] = newTargetList;
      return {
        ...state,
        ...getUpdateSourceAndFilteredData(newSourceData, newTargetList, mode, selectedItemsThreshold, state.sourceSearchValue, state.key),
        targetData: targetDataArr,
        currentActionQueue: [],
        itemSelections: newSelectedRowsArray,
        targetFilteredData: [targetFilteredData] // there will be only one target list in case of custom items
      };

    case 'reset':
      return {
        ...state,
        ...action.payload,
        ...getUpdateSourceAndFilteredData(action.payload.sourceData, action.payload.targetData[0], mode, selectedItemsThreshold, action.payload.sourceSearchValue, action.payload.key)
      };
    default:
      onTargetDataChange(newTargetData, undefined, state.selectedTableIndex);
      return {
        ...state,
        ...getUpdateSourceAndFilteredData(state.sourceData, state.targetData[0], mode, selectedItemsThreshold, state.sourceSearchValue, state.key),
        targetData: state.targetData,
        currentActionQueue: []
      };
  }
}
const escapeRegExp = text => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};
function moveItems(inputArray, from, to) {
  let numberOfDeletedItems = 1;
  const item = inputArray.splice(from, numberOfDeletedItems)[0];
  numberOfDeletedItems = 0;
  inputArray.splice(to, numberOfDeletedItems, item);
  return inputArray;
}

/* -------------------------------------------
 *  Return the next row to be selected after items are moved
 * ---------------------------------------------*/

function calculateNextSelectableItemIndices(selectedRowsNumberArray, newData, displayStyle) {
  const newSelectedRowsArray = [];
  if (selectedRowsNumberArray.length === 1) {
    if (displayStyle === 'single') {
      // If the display style is single, we need to calculate the next selectable items indices in a different way
      const nextSelectableIndex = calculateNextSelectableItemIndexInSingleDisplayMode(selectedRowsNumberArray, newData);
      newSelectedRowsArray.push(nextSelectableIndex);
    } else {
      if (selectedRowsNumberArray[0] === newData.length - 1) {
        newSelectedRowsArray.push(selectedRowsNumberArray[0] - 1);
      } else {
        newSelectedRowsArray.push(selectedRowsNumberArray[0]);
      }
    }
  } else if (selectedRowsNumberArray.length > 1) {
    if (lastItemIsSelected(selectedRowsNumberArray, newData)) {
      if (selectionIsContiguous(selectedRowsNumberArray)) {
        selectedRowsNumberArray.sort(compareAscendingOrder);
        newSelectedRowsArray.push(selectedRowsNumberArray[0] - 1);
      } else {
        let updatedIndex = 0;
        const lastValue = selectedRowsNumberArray[selectedRowsNumberArray.length - 1];
        const result = selectedRowsNumberArray.filter(value => value <= lastValue);
        if (lastValue === newData.length - 1) {
          updatedIndex = lastValue - 1 - (selectedRowsNumberArray.length - 1);
        } else {
          updatedIndex = lastValue + 1 - result.length;
        }
        newSelectedRowsArray.push(updatedIndex);
      }
    } else {
      if (selectionIsContiguous(selectedRowsNumberArray)) {
        selectedRowsNumberArray.sort(compareAscendingOrder);
        newSelectedRowsArray.push(selectedRowsNumberArray[0]);
      } else {
        let updatedIndex = 0;
        const lastValue = selectedRowsNumberArray[selectedRowsNumberArray.length - 1];
        const result = selectedRowsNumberArray.filter(value => value <= lastValue);
        updatedIndex = lastValue + 1 - result.length;
        newSelectedRowsArray.push(updatedIndex);
      }
    }
  }
  return newSelectedRowsArray.map(String);
}

/**
 * Function to calculate the next selectable indices in 'single' display mode
 * @param selectedRowsNumberArray - The array of indices of the selected elements
 * @param newData - The target table data before the delete operation
 * @returns - The next selectable item index
 */
function calculateNextSelectableItemIndexInSingleDisplayMode(selectedRowsNumberArray, newData) {
  // We take the first item from the selected list of items to calculate the next selectable index
  const firstItem = newData[selectedRowsNumberArray[0]];
  // Take the index of the first occurence of the selected item in the target data
  const itemIndex = newData.findIndex(targetItem => JSON.stringify(targetItem) === JSON.stringify(firstItem));
  /**
   * If the item was present in the last index of the target data,
   * we return (itemIndex - 1) so that the last element in the newly created target data will be selected.
   * Else we take the same index as in the old target data.
   */
  return itemIndex === newData.length - 1 ? itemIndex - 1 : itemIndex;
}

/* -------------------------------------------
 *  Check if the last item is a part of multiple selection
 * ---------------------------------------------*/

const lastItemIsSelected = (selectedRowsNumberArray, newData) => {
  if (selectedRowsNumberArray && selectedRowsNumberArray.some(value => value === newData.length - 1)) {
    return true;
  } else {
    return false;
  }
};

/* -------------------------------------------
 *  Check if selection is contiguous when multiple items selected
 * ---------------------------------------------*/

const selectionIsContiguous = selectedRowsNumberArray => {
  const selectedRowsArray = [...selectedRowsNumberArray];
  selectedRowsArray.sort(compareAscendingOrder);
  for (let index = 0; index < selectedRowsArray.length - 1; index++) {
    if (Math.abs(selectedRowsArray[index] - selectedRowsArray[index + 1]) !== 1) {
      return false;
      break;
    }
  }
  return true;
};
function filterData(filteredData, originalData) {
  return originalData.filter(item => !filteredData.includes(item));
}
/* -------------------------------------------
 *  Perform action for Add / Remove
 * ---------------------------------------------*/
function moveButtonAction(sourceData, targetData, selectedRowsNumberArray, displayStyle) {
  /* -------------------------------------------
   *  Set selected row for source
   * ---------------------------------------------*/
  const newSelectedRowsArray = calculateNextSelectableItemIndices(selectedRowsNumberArray, sourceData, displayStyle);
  for (const index of selectedRowsNumberArray) {
    if (sourceData[index]) {
      targetData.push(sourceData[index]);
    }
  }
  const newSourceData = filterData(targetData, sourceData);
  return {
    newSourceData,
    newTargetData: targetData,
    newSelectedRowsArray
  };
}
function compareAscendingOrder(a, b) {
  return a - b;
}
function compareDescendingOrder(a, b) {
  return b - a;
}
function handleAddAction(preState, payload, onTargetChange, selectedItemsThreshold) {
  const {
    selectedRows,
    isMoveAll: isAll,
    mode: sourceDataProcessMode
  } = payload;
  const {
    sourceData,
    sourceFilteredData,
    sourceSearchValue,
    targetData,
    targetSearchValue,
    key
  } = preState;
  const targetList = targetData[payload.layoutIndex ?? 0];
  const shouldApplySelectedItemsThreshold = !isNullish(selectedItemsThreshold) && selectedItemsThreshold > 0;
  if (shouldApplySelectedItemsThreshold) {
    if (isAll && (sourceFilteredData ?? sourceData).filter(item => !item._disabled).length + targetList.length > selectedItemsThreshold ||
    // selectedRows appropriately seems like Array<string>; but not sure why typed as any
    (selectedRows?.length ?? 0) + targetList.length > selectedItemsThreshold) {
      return preState;
    }
  }
  const actionQueueItem = generateAddActionQueueItem(preState, selectedRows, isAll, payload.layoutIndex ?? 0);
  const source = sourceFilteredData ?? sourceData;
  const selectedItemIndices = getSelectedItemIndices(selectedRows, source, isAll);
  const selectedItems = getSelectedSourceItems(sourceFilteredData ?? sourceData, selectedItemIndices, isAll);
  const newTargetData = addItemsToTargetData(selectedItems, targetList);
  const newSourceData = getSourceItemsWithDisabledStateUpdate(sourceDataProcessMode === 'move' ? removeItemsInSource(selectedItems, sourceData) : sourceData, newTargetData, sourceDataProcessMode, selectedItemsThreshold);
  const newSourceSearchValue = newSourceData.length > 0 ? sourceSearchValue : '';
  const newSourceFilteredData = filteDataBySearchValueInField(newSourceData, newSourceSearchValue, key);
  const newTargetFilteredData = filteDataBySearchValueInField(newTargetData, targetSearchValue?.[payload.layoutIndex ?? 0], key);
  const newSelections = shouldApplySelectedItemsThreshold && newTargetData.length === selectedItemsThreshold ? undefined : getNewSelections(selectedRows, source, sourceDataProcessMode, isAll);
  const newNarrator = {
    selectedRows: selectedRows.length,
    direction: 'add',
    action: isAll ? 'moveall' : 'move'
  };
  onTargetChange(newTargetData, undefined, payload.layoutIndex);
  const newData = [...targetData];
  newData[payload.layoutIndex ?? 0] = newTargetData;
  const targetFilteredDataArr = preState.targetFilteredData && [...preState.targetFilteredData];
  if (newTargetFilteredData && targetFilteredDataArr) targetFilteredDataArr[payload.layoutIndex ?? 0] = newTargetFilteredData;
  return {
    ...preState,
    sourceData: newSourceData,
    targetData: newData,
    sourceFilteredData: newSourceFilteredData,
    targetFilteredData: targetFilteredDataArr,
    sourceSearchValue: newSourceSearchValue,
    currentActionQueue: [actionQueueItem],
    itemSelections: newSelections,
    narrator: newNarrator,
    selectionTable: 'source',
    selectedTableIndex: payload.layoutIndex
  };
}
function handleRemoveAction(preState, payload, onTargetChange, selectedItemsThreshold) {
  const {
    selectedRows,
    isMoveAll: isAll,
    mode: sourceDataProcessMode,
    targetDisplayStyle: displayStyle = 'multiple',
    layoutIndex = 0
  } = payload;
  const {
    sourceData,
    sourceSearchValue,
    targetData,
    targetFilteredData,
    targetSearchValue,
    key,
    customItems
  } = preState;
  const target = targetFilteredData?.[layoutIndex] ?? targetData[layoutIndex];
  const indexMapFromFilterToTotal = generateIndexMapFromFilteredToTotal(targetFilteredData?.[layoutIndex], targetData[layoutIndex]);
  const indexMapFromCustomToTotal = generateIndexMapFromFilteredToTotal(customItems, targetData[layoutIndex]);
  const selectedItemIndices = getSelectedItemIndices(selectedRows, target, isAll);
  const selectedItems = getSelectedTargetItems(selectedItemIndices, target);
  const selectedRemovableItemIndices = filterOutRemovableItemIndicesFrom(selectedItemIndices, target);
  const newTargetData = removeItemsInTarget(getIndicesInTotal(selectedRemovableItemIndices, indexMapFromFilterToTotal), targetData[layoutIndex]);
  const newTargetFilteredData = newTargetData.length > 0 && targetFilteredData?.[layoutIndex] ? removeItemsInTarget(selectedRemovableItemIndices, targetFilteredData[layoutIndex]) : undefined;
  const selectedCustomItemIndices = filterOutCustomItemIndicesFrom(selectedItemIndices, target);
  const indicesInCustom = getIndicesInCustom(getIndicesInTotal(selectedCustomItemIndices, indexMapFromFilterToTotal), indexMapFromCustomToTotal);
  const newCustomItems = selectedCustomItemIndices.length > 0 && newTargetData.length > 0 && customItems ? removeItemsInTarget(indicesInCustom, customItems) : customItems;
  const selectedMovableItems = filterOutMovableItemsFrom(selectedItems);
  const newSourceData = getSourceItemsWithDisabledStateUpdate(sourceDataProcessMode === 'move' ? addItemsInSource(selectedMovableItems, sourceData) : sourceData, newTargetData, sourceDataProcessMode, selectedItemsThreshold);
  const actionQueueItem = generateRemoveActionQueueItem(preState, selectedRows.map(row => parseInt(row)), isAll, selectedCustomItemIndices, payload.layoutIndex ?? 0);
  const selectedUndoableItems = filterOutUndoableItemIndicesFrom(selectedItemIndices, target);
  const newSourceFilteredData = filteDataBySearchValueInField(newSourceData, sourceSearchValue, key);
  const newSelectedItemIndex = getNextSelectableTargetItemIndex(selectedItemIndices, target, displayStyle);
  const newTargetSearchValue = newTargetData.length > 0 ? targetSearchValue?.[layoutIndex] : '';
  const newNarrator = {
    selectedRows: selectedRows.length,
    direction: 'remove',
    action: isAll ? 'moveall' : 'move'
  };
  onTargetChange(newTargetData, undefined, layoutIndex);
  const newData = [...targetData];
  newData[layoutIndex] = newTargetData;
  const targetFilteredDataArr = preState.targetFilteredData && [...preState.targetFilteredData];
  if (targetFilteredDataArr) targetFilteredDataArr[payload.layoutIndex ?? 0] = newTargetFilteredData;
  const targetSearchValueArr = preState.targetSearchValue && [...preState.targetSearchValue];
  if (targetSearchValueArr) {
    targetSearchValueArr[layoutIndex] = newTargetSearchValue;
  }
  return {
    ...preState,
    sourceData: newSourceData,
    sourceFilteredData: newSourceFilteredData,
    targetData: newData,
    targetSearchValue: targetSearchValueArr,
    targetFilteredData: targetFilteredDataArr,
    customItems: newCustomItems,
    currentActionQueue: selectedUndoableItems.length > 0 ? [actionQueueItem] : [],
    narrator: newNarrator,
    itemSelections: newSelectedItemIndex !== undefined ? [newSelectedItemIndex.toString()] : undefined,
    selectionTable: 'target',
    selectedTableIndex: layoutIndex
  };
}
function generateIndexMapFromFilteredToTotal(filtered, total = []) {
  if (!filtered || filtered.length === 0) {
    return total.map((_item, index) => index);
  } else {
    const result = [];
    let indexInTotal = 0;
    for (let indexInFiltered = 0; indexInFiltered < filtered.length; indexInFiltered++) {
      for (; indexInTotal < total.length; indexInTotal++) {
        if (JSON.stringify(filtered[indexInFiltered]) === JSON.stringify(total[indexInTotal])) {
          result[indexInFiltered] = indexInTotal++;
          break;
        }
      }
    }
    return result;
  }
}
function getIndicesInTotal(indicesInFiltered, mapFromFilterToTotal) {
  return indicesInFiltered.map(value => mapFromFilterToTotal[value]);
}
function getIndicesInCustom(indicesForCustomInTotal, mapFromCustomToTotal) {
  return indicesForCustomInTotal.map(value1 => mapFromCustomToTotal.findIndex(value2 => value2 === value1));
}
function getSelectedItemIndices(selectedRows, items, isAll) {
  return isAll ? items.map((_item, index) => index) : selectedRows.map(item => parseInt(item));
}
function shallowCloneItems(source) {
  return source.map(item => {
    return {
      ...item
    };
  });
}
function generateAddActionQueueItem(state, selectedRows, isAll, layoutIndex) {
  const {
    sourceData,
    targetData,
    sourceFilteredData,
    targetFilteredData
  } = state;
  const targetList = targetData[layoutIndex ?? 0];
  const filteredTargetList = targetFilteredData?.[layoutIndex ?? 0];
  return {
    from: 'source',
    button: isAll ? 'addAll' : 'add',
    currentSourceData: shallowCloneItems(sourceData),
    currentTargetData: shallowCloneItems(targetList),
    lastItemSelection: [...selectedRows],
    lastSourceFilteredData: sourceFilteredData ? shallowCloneItems(sourceFilteredData) : undefined,
    lastTargetFilteredData: filteredTargetList ? shallowCloneItems(filteredTargetList) : undefined,
    selectedTableIndex: layoutIndex
  };
}
function getSelectedSourceItems(sourceData, selectedItemIndices, isAll = false) {
  return (isAll ? sourceData.filter(item => !(item._disabled ?? false)) : selectedItemIndices.map(itemIndex => sourceData[itemIndex])).filter(item => isItemSelectable(item));
}
function removeItemsInSource(items, source) {
  return source.filter(item => !items.includes(item));
}
function addItemsToTargetData(items, targetData) {
  return [...targetData, ...items];
}
function filteDataBySearchValueInField(data, searchValue, field) {
  if (searchValue && field) {
    searchValue = escapeRegExp(searchValue);
    const regExp = new RegExp(searchValue, 'gi');
    return data.filter(item => {
      return item[field].match(regExp) || item.hasChildren;
    });
  } else {
    return undefined;
  }
}
function getSelectedTargetItems(selectedItemIndices, target) {
  return selectedItemIndices.map(rowIndex => target[rowIndex]);
}
function filterOutMovableItemsFrom(items) {
  return items.filter(item => !(item._alwaysSelected || item._custom));
}
function filterOutRemovableItemIndicesFrom(indices, items) {
  return indices.filter(itemIndex => !items[itemIndex]._alwaysSelected);
}
function filterOutCustomItemIndicesFrom(indices, items) {
  return indices.filter(itemIndex => items[itemIndex]._custom);
}
function filterOutUndoableItemIndicesFrom(indices, items) {
  return indices.filter(itemIndex => !items[itemIndex]._custom);
}
function removeItemsInTarget(itemIndices, target) {
  const newTarget = target.filter((_, index) => !itemIndices.includes(index));
  return newTarget;
}
function addItemsInSource(items, source) {
  return [...source, ...items];
}
function generateRemoveActionQueueItem(state, selectedItemIndices, isAll, customSelectedItems, layoutIndex) {
  const {
    sourceData,
    targetData,
    sourceFilteredData,
    targetFilteredData
  } = state;
  const selectedRowsWithoutCustomIndices = regenerateSelectedRows(customSelectedItems, selectedItemIndices);
  const targetFilteredList = targetFilteredData?.[layoutIndex ?? 0];
  let targetList = targetData[layoutIndex ?? 0];
  // If the targetFiltered list is not empty, we remove only the filtered custom items from the original target list
  if (!!targetFilteredList && targetFilteredList.length > 0) {
    targetList = targetList.filter(item => !(item._custom && targetFilteredList.some(targetItem => JSON.stringify(targetItem) === JSON.stringify(item))));
  } else {
    targetList = removeItemsInTarget(customSelectedItems, shallowCloneItems(targetList));
  }
  return {
    from: 'target',
    button: isAll ? 'removeAll' : 'remove',
    currentSourceData: shallowCloneItems(sourceData),
    currentTargetData: targetList,
    lastItemSelection: selectedRowsWithoutCustomIndices,
    lastSourceFilteredData: sourceFilteredData ? shallowCloneItems(sourceFilteredData) : undefined,
    lastTargetFilteredData: targetFilteredList ? removeItemsInTarget(customSelectedItems, shallowCloneItems(targetFilteredList)) : undefined,
    selectedTableIndex: layoutIndex
  };
}
function regenerateSelectedRows(customIndices, selectedIndices) {
  const remain = selectedIndices.filter(row => !customIndices.includes(row));
  let final = remain;
  const customIndicesWithDescendingOrder = [...customIndices].sort(compareDescendingOrder);
  customIndicesWithDescendingOrder.forEach(removedIndex => {
    final = final.map(remainIndex => remainIndex > removedIndex ? remainIndex - 1 : remainIndex);
  });
  return final.map(row => row.toString());
}
function getNewSelections(selectedRows, sourceItems, mode, isSelectedAll) {
  let selectedItemIndices, lastSelectedItemIndex, nextSelectableItemIndex, disabledItemIndices;
  switch (mode) {
    case 'move':
      selectedItemIndices = getSelectedItemIndices(selectedRows, sourceItems, isSelectedAll);
      lastSelectedItemIndex = selectedItemIndices[selectedItemIndices.length - 1];
      const unselectableItemIndices = getUnselectableItemIndices(sourceItems);
      nextSelectableItemIndex = getNearestItemIndexInMultipleDisplayStyle(lastSelectedItemIndex, selectedItemIndices, sourceItems.length, true, unselectableItemIndices);
      return nextSelectableItemIndex === undefined ? undefined : [nextSelectableItemIndex.toString()];
    case 'disable':
      selectedItemIndices = getSelectedItemIndices(selectedRows, sourceItems, isSelectedAll);
      lastSelectedItemIndex = selectedItemIndices[selectedItemIndices.length - 1];
      disabledItemIndices = getDisabledItemIndices(sourceItems);
      nextSelectableItemIndex = getNearestItemIndexInMultipleDisplayStyle(lastSelectedItemIndex, [...selectedItemIndices, ...disabledItemIndices], sourceItems.length, false);
      return nextSelectableItemIndex === undefined ? undefined : [nextSelectableItemIndex.toString()];
    case 'keep':
      selectedItemIndices = getSelectedItemIndices(selectedRows, sourceItems, false);
      lastSelectedItemIndex = selectedItemIndices[selectedItemIndices.length - 1];
      const duplicableSelectedItemIndices = filterOutDuplicableItemIndices(selectedItemIndices, sourceItems);
      if (duplicableSelectedItemIndices.length > 0) {
        return duplicableSelectedItemIndices.map(itemIndex => itemIndex.toString());
      } else {
        selectedItemIndices = getSelectedItemIndices(selectedRows, sourceItems, isSelectedAll);
        const unduplicableSelectedItemIndices = filterOutUnduplicableItemIndices(selectedItemIndices, sourceItems);
        disabledItemIndices = getDisabledItemIndices(sourceItems);
        nextSelectableItemIndex = getNearestItemIndexInMultipleDisplayStyle(lastSelectedItemIndex, [...unduplicableSelectedItemIndices, ...disabledItemIndices], sourceItems.length, false);
        return nextSelectableItemIndex === undefined ? undefined : [nextSelectableItemIndex.toString()];
      }
  }
}
function filterOutDuplicableItemIndices(selectedIndices, source) {
  const result = [];
  selectedIndices.forEach(i => {
    if (!source[i].disallowDuplication) {
      result.push(i);
    }
  });
  return result;
}
function filterOutUnduplicableItemIndices(selectedIndices, source) {
  const result = [];
  selectedIndices.forEach(i => {
    if (source[i].disallowDuplication) {
      result.push(i);
    }
  });
  return result;
}
function getNearestItemIndexInMultipleDisplayStyle(destinationItemIndex, nonselectableItemIndices, totalItemCount, removeNonselectableItems, excludedItemIndices) {
  const totalItemIndices = getTotalItemIndicesWithSpecifiedItemsMarked(nonselectableItemIndices, excludedItemIndices || [], totalItemCount);
  let nearestItemIndex = getNearestItemIndex(destinationItemIndex, totalItemIndices);
  if (removeNonselectableItems && nearestItemIndex !== undefined) {
    const itemIndicesWithoutNonselectableItems = totalItemIndices.filter(rowIndex => rowIndex !== -1);
    nearestItemIndex = itemIndicesWithoutNonselectableItems.findIndex(rowIndex => rowIndex === nearestItemIndex);
  }
  return nearestItemIndex;
}
function getTotalItemIndicesWithSpecifiedItemsMarked(specifiedItemIndices, excludedItemIndices, totalItemsCount) {
  const totalItemIndices = generateTotalItemIndices(totalItemsCount);
  return markSpecifiedItemIndices(specifiedItemIndices, excludedItemIndices, totalItemIndices);
}
function generateTotalItemIndices(totalItemsCount) {
  const totalItemIndices = new Array(totalItemsCount);
  for (let i = 0; i < totalItemsCount; i++) {
    totalItemIndices[i] = i;
  }
  return totalItemIndices;
}
function markSpecifiedItemIndices(specifiedItemIndices, excludedItemIndices, indices) {
  const markedIndices = [...indices];
  specifiedItemIndices.forEach(rowIndex => {
    markedIndices[rowIndex] = -1;
  });
  excludedItemIndices.forEach(rowIndex => {
    markedIndices[rowIndex] = -2;
  });
  return markedIndices;
}
function getUnselectableItemIndices(items) {
  const unselectableItemIndices = [];
  items.forEach((item, index) => {
    if (!isItemSelectable(item)) {
      unselectableItemIndices.push(index);
    }
  });
  return unselectableItemIndices;
}
function getDisabledItemIndices(items) {
  const disabledItemIndices = [];
  items.forEach((item, index) => {
    if (item._disabled) {
      disabledItemIndices.push(index);
    }
  });
  return disabledItemIndices;
}
function getNearestItemIndex(destinationItemIndex, itemIndices) {
  let nearestItemIndex = getNearestLastItemIndex(destinationItemIndex, itemIndices);
  if (nearestItemIndex === undefined) {
    nearestItemIndex = getNearestPreviousItemIndex(destinationItemIndex, itemIndices);
  }
  return nearestItemIndex;
}
function getNearestLastItemIndex(destinationItemIndex, itemIndices) {
  for (let i = destinationItemIndex + 1; i < itemIndices.length; i++) {
    if (itemIndices[i] > -1) {
      return i;
    }
  }
  return undefined;
}
function getNearestPreviousItemIndex(destinationItemIndex, itemIndices) {
  for (let i = destinationItemIndex - 1; i >= 0; i--) {
    if (itemIndices[i] > -1) {
      return i;
    }
  }
  return undefined;
}
function getNextSelectableTargetItemIndex(removedItemIndices, items, displayStyle) {
  const destinationItemIndex = removedItemIndices[removedItemIndices.length - 1];
  switch (displayStyle) {
    case 'multiple':
      return getNearestItemIndexInMultipleDisplayStyle(destinationItemIndex, removedItemIndices, items.length, true);
    case 'single':
      return getNearestItemIndexInSingleDisplayStyle(destinationItemIndex, removedItemIndices, items);
  }
}
function getNearestItemIndexInSingleDisplayStyle(destinationItemIndex, removedItemIndices, items) {
  const nearesItem = getNearestItemInSingleDisplayStyle(destinationItemIndex, removedItemIndices, items);
  const itemsWithoutRemovedItems = items.filter((_, index) => !removedItemIndices.includes(index));
  if (nearesItem !== undefined) {
    const nearestItemString = JSON.stringify(nearesItem);
    for (let i = itemsWithoutRemovedItems.length - 1; i > -1; i--) {
      if (JSON.stringify(itemsWithoutRemovedItems[i]) === nearestItemString) {
        return i;
      }
    }
  }
  return undefined;
}
function getNearestItemInSingleDisplayStyle(destinationItemIndex, removedItemIndices, items) {
  const retainedItemsWithDestinationItem = items.filter((_, index) => !removedItemIndices.includes(index) || index === destinationItemIndex);
  const displayItems = generateSingleDisplayData(retainedItemsWithDestinationItem);
  const destinationItem = items[destinationItemIndex];
  const destinationDisplayItemIndex = displayItems.findIndex(displayItem => JSON.stringify(displayItem.data) === JSON.stringify(destinationItem));
  if (displayItems[destinationDisplayItemIndex].count > 1) {
    return destinationItem;
  }
  const nearestDiplayItemIndex = getNearestItemIndexInMultipleDisplayStyle(destinationDisplayItemIndex, [destinationDisplayItemIndex], displayItems.length, false);
  return nearestDiplayItemIndex !== undefined ? displayItems[nearestDiplayItemIndex].data : undefined;
}

/**
 * returns array of items post handling the state for disable and keep mode.
 *
 * @param sourceItems
 * @param targetItems
 * @param action
 * @param mode
 * @param selectedItemThreshold
 */
function getSourceItemsWithDisabledStateUpdate(sourceItems, targetItems, mode, selectedItemsThreshold) {
  sourceItems = shallowCloneItems(sourceItems);
  const isThresholdMet = !isNullish(selectedItemsThreshold) && selectedItemsThreshold > 0 && targetItems.length >= selectedItemsThreshold;
  if (isThresholdMet) {
    sourceItems.forEach(srcItem => srcItem._disabled = true);
  } else {
    switch (mode) {
      case 'move':
        sourceItems.forEach(srcItem => delete srcItem._disabled);
        break;
      case 'disable':
      case 'keep':
        for (const sourceItem of sourceItems) {
          const matchingIndex = targetItems.findIndex(targetItem => {
            const {
              _disabled: a,
              disallowDuplication: a1,
              _alwaysSelected: a2,
              ...restOfsourceItem
            } = sourceItem;
            const {
              _disabled: b,
              disallowDuplication: b1,
              _alwaysSelected: b2,
              ...restOfTargetItem
            } = targetItem;
            return JSON.stringify(restOfsourceItem) === JSON.stringify(restOfTargetItem);
          });
          if (matchingIndex > -1) {
            if (mode === 'disable' || sourceItem.disallowDuplication) {
              sourceItem._disabled = true;
            } else {
              delete sourceItem._disabled;
            }
          } else {
            delete sourceItem._disabled;
          }
        }
        break;
    }
  }
  return sourceItems;
}
function getUpdateSourceAndFilteredData(sourceData, targetData, mode, selectedItemsThreshold, sourceSearchValue, fieldName) {
  sourceData = getSourceItemsWithDisabledStateUpdate(sourceData, targetData, mode, selectedItemsThreshold);
  const sourceFilteredData = filteDataBySearchValueInField(sourceData, sourceSearchValue, fieldName);
  return {
    sourceData,
    sourceFilteredData
  };
}
function removeInternalFields(items) {
  return items.map(item => {
    const {
      _custom,
      _disabled,
      _alwaysSelected,
      ...rest
    } = item;
    return rest;
  });
}
//# sourceMappingURL=DataManagement.js.map